import { getFilesURLs, newSingleFileDownload } from '~src/services/api';
import { saveFromLink } from '~src/utils/downloader/shared/saveFromLink';
import { DownloadingState, useStore } from '~src/store';
import { message, Modal } from 'antd';
import streamSaver from 'streamsaver';
import { appLog } from '~src/utils/log';

interface newFileType {
  file_id: string;
  file_name: string;
  file_size: number;
  folder_id: number;
  using_key: string;
}

// Detect if browser is on a mobile device
const isMobileDevice = () => {
  const ua = navigator.userAgent;
  return (
    /Android/i.test(ua) ||
    /iPad|iPhone|iPod/.test(ua) ||
    /Mobile/i.test(ua) ||
    /Tablet/i.test(ua)
  );
};

// Page refresh download warning
const activeDownloads = new Set<string>();

const beforeUnloadHandler = (event: BeforeUnloadEvent) => {
  if (activeDownloads.size > 0) {
    event.preventDefault();
    // Standard way to show a confirmation prompt in modern browsers.
    event.returnValue =
      'Downloads are in progress. Are you sure you want to leave?';
  }
};

// off warning of reload which unpredictable on mobiles
if (!isMobileDevice()) {
  window.addEventListener('beforeunload', beforeUnloadHandler);
}

// confirmation modal for downloading large single files
const ModalConfirmDownloadSingleFile = (fileSize: number): Promise<boolean> => {
  const sizeInGB = (fileSize / 1024 / 1024 / 1024).toFixed(2);
  function onCancelFunction() {
    useStore.getState().changeDownloadingState('FINISHING');
    setTimeout(() => useStore.getState().changeDownloadingActive(false), 1500);
  }
  return new Promise((resolve) => {
    Modal.confirm({
      title: 'Warning',
      content: `You are about to download a single file of ${sizeInGB} GB. This may take a significant amount of time and browser resources. Do you want to continue?`,
      onOk: () => resolve(true),
      onCancel: () => {
        onCancelFunction();
        resolve(false);
      },
      okText: 'Continue',
      cancelText: 'Cancel',
    });
  });
};

/** Download single file selected version */
export const downloadFileLastVersion = async (
  file,
  fileVersionsContext,
  onDownloadStarted?: () => unknown
) => {
  const isPanelActive = useStore.getState().isDownloadingActive;
  const messageKey = `download-progress-${file.id}-${Date.now()}`;

  if (isPanelActive) {
    useStore.getState().changeDownloadingState(DownloadingState.DOWNLOADING);
  }

  if (!file.id) throw new Error("No file's id");

  activeDownloads.add(file.id);

  try {
    let selectedRevision = 1;

    // For desktop: Use the selected version from context
    const selectedVersion = fileVersionsContext[file.id];

    // If fileVersionsContext has a selected version, use it
    // Otherwise, if file has versions array, use the first version (latest)
    // If neither is available, default to revision 1
    if (selectedVersion) {
      selectedRevision = selectedVersion.revision;
    } else if (file.versions && file.versions.length > 0) {
      // On mobile, fileVersionsContext is empty, so use the latest version from file.versions
      selectedRevision = file.versions[0].revision;

      // console.log(`selectedVersion:`, {
      //   file,
      //   selectedVersion,
      //   fileVersionsContext,
      // });
    }

    if (isPanelActive) {
      onDownloadStarted?.();
    }

    // Step 1: Get file metadata with using_key
    const registrationKeys: newFileType[] = await newSingleFileDownload(
      'registration_keys',
      {
        projectId: Number(file.project_id),
        registration_ids: file.id ? Number(file.id) : undefined,
        // @ts-ignore
        serial: selectedRevision,
      }
    );

    if (!registrationKeys.length || !registrationKeys[0].using_key)
      throw new Error('registrationIds using_key not found');

    const fileMetadata = registrationKeys[0];
    const fileName = fileMetadata.file_name;
    const fileSize = fileMetadata.file_size;

    // Step 2: Get download URL using the using_key
    const fileUrl = await getFilesURLs(
      String(file.project_id),
      registrationKeys.map((key) => key.using_key)
    );

    const fileData = fileUrl[0];
    if (!fileData || !fileData.url)
      throw new Error('Invalid file URL received');

    const downloadUrl = fileData.url;

    if (isMobileDevice()) {
      // HTTP CODE ERROR HANDLING for Mobiles
      if (!isPanelActive) {
        message.loading({
          content: 'Verifying file...',
          key: messageKey,
          duration: 0,
        });
      }

      // Create an AbortController to cancel the request after headers are received.
      const controller = new AbortController();

      // Initiate a GET request (the correct method) but pass the abort signal.
      const response = await fetch(downloadUrl, { signal: controller.signal });

      // We have received the headers and the status. We don't need to download
      // the body in JavaScript, so we abort the request immediately.
      controller.abort();

      if (!response.ok) {
        throw new Error(
          `File not available. Server responded with status: ${response.status}`
        );
      }

      // If the check succeeds, the URL is valid. Proceed with the ORIGINAL download logic.
      if (!isPanelActive) {
        message.success({
          content: 'Download starting...',
          key: messageKey,
          duration: 3,
        });
      }
      saveFromLink(downloadUrl, fileName);
      useStore.getState().changeDownloadingState(DownloadingState.FINISHING);
      setTimeout(
        () => useStore.getState().changeDownloadingActive(false),
        3000
      );
    } else {
      // Step 3: Download the file using streaming for ALL devices
      const LARGE_FILE_THRESHOLD = 2 * 1024 * 1024 * 1024; // 2 GB

      if (fileSize > LARGE_FILE_THRESHOLD) {
        const proceed = await ModalConfirmDownloadSingleFile(fileSize);
        if (!proceed) {
          // Must remove from active downloads if the user cancels
          activeDownloads.delete(file.id);
          return;
        }
      }

      if (!isPanelActive) {
        message.loading({
          content: 'Download starting...',
          key: messageKey,
          duration: 0,
        });
      }

      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      if (!response.body) {
        throw new Error('Response body is not readable.');
      }

      useStore.getState().changeTotalFileSize(fileSize);
      useStore.getState().totalDownloadFiles(1);
      useStore.getState().currentDownloadFile(0);

      const fileStream = streamSaver.createWriteStream(fileName, {
        size: fileSize,
      });
      const writer = fileStream.getWriter();
      const reader = response.body.getReader();

      let downloadedBytes = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        if (value) {
          await writer.write(value);
          downloadedBytes += value.length;
          const progressPercentage = Math.round(
            (downloadedBytes / fileSize) * 100
          );

          // Conditional progress update based on context
          if (isPanelActive) {
            useStore.getState().totalDownloadSize(progressPercentage);
          } else {
            message.loading({
              content: `Downloading ${fileName}: ${progressPercentage}%`,
              key: messageKey,
              duration: 0,
            });
          }
        }
      }
      await writer.close();

      if (isPanelActive) {
        useStore.getState().currentDownloadFile(1);
        useStore.getState().changeDownloadingState(DownloadingState.FINISHING);
        setTimeout(
          () => useStore.getState().changeDownloadingActive(false),
          3000
        );
      } else {
        message.success({
          content: `${fileName} downloaded successfully!`,
          key: messageKey,
          duration: 3,
        });
      }
    }
  } catch (error: any) {
    if (!isPanelActive) {
      if (error.name === 'AbortError') {
        appLog('Download aborted.');
        message.destroy(messageKey);
      } else {
        message.error({
          content: `Download failed: ${error?.message || 'Unknown error'}`,
          key: messageKey,
          duration: 5,
        });
      }
    }

    console.error(`Download failed: ${error?.message || 'Unknown error'}`);
    useStore.getState().changeDownloadingState(DownloadingState.FINISHING);
    setTimeout(() => useStore.getState().changeDownloadingActive(false), 3000);
  } finally {
    activeDownloads.delete(file.id);
  }
};
